import { ClientCombobox } from "@/components/ClientCombobox";
import { useEffect, useState, useRef } from "react";
import { DropZone } from "@/components/DropZone";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadIcon as Upload, FilePdfIcon as FilePdf } from "@phosphor-icons/react";
import { useCrmConfig, CrmSelect } from "@/components/CrmConfig";

export const PREV_STATI = ["Bozza", "Inviato al cliente", "In trattativa", "Accettato", "Perso", "Scaduto", "Sostituito", "Annullato"];

const empty = (opp) => ({
  opportunita_id: opp?.id || "", cliente_id: opp?.cliente_id || "", numero_preventivo: "", data_preventivo: new Date().toISOString().slice(0, 10),
  data_scadenza: "", oggetto: "", categoria: opp?.categoria || "", origine: opp?.origine || "", importo: 0, valuta: "EUR",
  stato: "Bozza", responsabile: opp?.responsabile || "", visibile_area_cliente: false, accettabile_online: false, note_interne: "",
});

export function PreventivoDialog({ open, onOpenChange, clients, opportunita, fixedOpp, editing, onSaved }) {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const crm = useCrmConfig();

  useEffect(() => {
    if (!open) return;
    if (editing) setForm({ ...empty(), ...editing });
    else setForm(empty(fixedOpp));
    setFile(null);
  }, [open, editing, fixedOpp]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // opportunità selezionabili in base al cliente
  const oppForClient = (opportunita || []).filter((o) => !form.cliente_id || o.cliente_id === form.cliente_id);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, importo: Number(form.importo) || 0 };
      let data;
      if (editing) ({ data } = await api.put(`/crm/preventivi/${editing.id}`, payload));
      else ({ data } = await api.post("/crm/preventivi", payload));
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/crm/preventivi/${data.id}/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editing ? "Preventivo aggiornato" : "Preventivo creato");
      onOpenChange(false);
      onSaved && onSaved(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="preventivo-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifica preventivo" : "Nuovo preventivo"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Cliente</Label>
            <div className="mt-1"><ClientCombobox clients={clients} value={form.cliente_id || "none"} disabled={!!fixedOpp} placeholder="Nessuno (non associato)" extraOptions={[{ value: "none", label: "Nessuno (non associato)" }]} onChange={(v) => { const cid = v === "none" ? "" : v; setForm((f) => ({ ...f, cliente_id: cid, opportunita_id: fixedOpp ? f.opportunita_id : "" })); }} testid="prev-cliente" /></div>
            <p className="text-[11px] text-muted-foreground mt-1">Facoltativo: puoi associarlo in seguito.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Opportunità collegata</Label>
            <Select value={form.opportunita_id || "none"} onValueChange={(v) => set("opportunita_id", v === "none" ? "" : v)} disabled={!!fixedOpp}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="prev-opportunita"><SelectValue placeholder="Nessuna" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessuna</SelectItem>
                {oppForClient.map((o) => <SelectItem key={o.id} value={o.id}>{o.titolo || o.id.slice(0, 8)}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">Facoltativa.</p>
          </div>
          <div><Label className="text-xs uppercase tracking-wider">Numero preventivo</Label><Input value={form.numero_preventivo} onChange={(e) => set("numero_preventivo", e.target.value)} className="mt-1 rounded-sm" data-testid="prev-numero" /></div>
          <div><Label className="text-xs uppercase tracking-wider">Oggetto</Label><Input value={form.oggetto} onChange={(e) => set("oggetto", e.target.value)} className="mt-1 rounded-sm" data-testid="prev-oggetto" /></div>
          <div><Label className="text-xs uppercase tracking-wider">Data preventivo</Label><Input type="date" value={(form.data_preventivo || "").slice(0, 10)} onChange={(e) => set("data_preventivo", e.target.value)} className="mt-1 rounded-sm" data-testid="prev-data" /></div>
          <div><Label className="text-xs uppercase tracking-wider">Data scadenza</Label><Input type="date" value={(form.data_scadenza || "").slice(0, 10)} onChange={(e) => set("data_scadenza", e.target.value)} className="mt-1 rounded-sm" data-testid="prev-scadenza" /></div>
          <div><Label className="text-xs uppercase tracking-wider">Importo (€)</Label><Input type="number" step="0.01" value={form.importo} onChange={(e) => set("importo", e.target.value)} className="mt-1 rounded-sm" data-testid="prev-importo" /></div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Stato</Label>
            <Select value={form.stato} onValueChange={(v) => set("stato", v)}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="prev-stato"><SelectValue /></SelectTrigger>
              <SelectContent>{PREV_STATI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase tracking-wider">Categoria</Label><CrmSelect value={form.categoria} onChange={(v) => set("categoria", v)} options={crm.categorie} placeholder="Seleziona categoria" testid="prev-categoria" /></div>
          <div><Label className="text-xs uppercase tracking-wider">Origine</Label><CrmSelect value={form.origine} onChange={(v) => set("origine", v)} options={crm.origini} placeholder="Seleziona origine" testid="prev-origine" /></div>
          <div><Label className="text-xs uppercase tracking-wider">Commerciale</Label><CrmSelect value={form.responsabile} onChange={(v) => set("responsabile", v)} options={crm.commerciali} placeholder="Seleziona commerciale" testid="prev-responsabile" /></div>
          <div className="flex items-center gap-3 mt-6"><Switch checked={!!form.visibile_area_cliente} onCheckedChange={(v) => set("visibile_area_cliente", v)} data-testid="prev-visibile" /><Label className="text-sm">Visibile in area cliente</Label></div>
          <div className="flex items-center gap-3 mt-6 opacity-60"><Switch checked={false} disabled data-testid="prev-accettabile" /><Label className="text-sm">Accettabile online <span className="text-xs text-muted-foreground">(funzione futura)</span></Label></div>
          <div className="col-span-2"><Label className="text-xs uppercase tracking-wider">Note interne</Label><Textarea value={form.note_interne} onChange={(e) => set("note_interne", e.target.value)} rows={2} className="mt-1 rounded-sm" data-testid="prev-note" /></div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider">PDF preventivo (max 25 MB)</Label>
            <DropZone onFiles={(fl) => setFile(fl?.[0] || null)} className="block">
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="prev-pdf-input" />
            <div className="mt-1 flex items-center gap-2">
              <Button type="button" variant="outline" className="rounded-sm gap-2" onClick={() => fileRef.current?.click()} data-testid="prev-pdf-btn"><Upload size={16} /> Scegli PDF</Button>
              {file ? <span className="text-sm inline-flex items-center gap-1"><FilePdf size={16} className="text-primary" /> {file.name}</span>
                : editing?.pdf_filename ? <span className="text-sm inline-flex items-center gap-1 text-muted-foreground"><FilePdf size={16} /> {editing.pdf_filename} (già caricato)</span>
                : <span className="text-sm text-muted-foreground">Nessun file</span>}
            </div>
            </DropZone>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="prev-save-btn">{saving ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
